import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { isChatQuotaExceeded } from "@/contexts/PaywallContext";
import RewardedAdModal from "@/components/RewardedAdModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles, MessageCircle, Plus, Clock, PlayCircle, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { createRecognizer, speechRecognitionSupported, speechSynthesisSupported, speak, stopSpeaking } from "@/lib/voice";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AstroChat() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const guard = usePaywallGuard();
  const STARTERS = [
    t("chat.starter.promotion"),
    t("chat.starter.lifepath"),
    t("chat.starter.lucky_today"),
    t("chat.starter.mangal"),
  ];
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // ---- Voice: mic (STT) + speaker (TTS) ----
  const [listening, setListening] = useState(false);
  const [voiceReplyOn, setVoiceReplyOn] = useState(() => {
    try { return localStorage.getItem("cs_voice_reply") === "1"; } catch { return false; }
  });
  const recognizerRef = useRef(null);
  const sttSupported = speechRecognitionSupported();
  const ttsSupported = speechSynthesisSupported();

  const toggleMic = () => {
    if (!sttSupported) {
      setError("Voice input isn't supported in this browser. Try Chrome/Edge/Safari on desktop or Android.");
      return;
    }
    if (listening && recognizerRef.current) {
      try { recognizerRef.current.stop(); } catch (_) { /* noop */ }
      return;
    }
    const rec = createRecognizer(lang);
    if (!rec) return;
    rec.onresult = (ev) => {
      let interim = "";
      let final = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      setInput((final || interim).trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognizerRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  const toggleVoiceReply = () => {
    const next = !voiceReplyOn;
    setVoiceReplyOn(next);
    try { localStorage.setItem("cs_voice_reply", next ? "1" : "0"); } catch (_) { /* noop */ }
    if (!next) stopSpeaking();
  };

  // Stop any ongoing speech when leaving the page
  useEffect(() => () => stopSpeaking(), []);

  const loadSessions = async () => {
    const { data } = await api.get("/chat/sessions");
    setSessions(data.sessions || []);
  };

  const refreshQuota = async (sid) => {
    if (!sid) { setQuota(null); return; }
    try {
      const { data } = await api.get("/chat/quota", { params: { session_id: sid } });
      setQuota(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { refreshQuota(currentSession); }, [currentSession]);

  // Tick quota every 30s while session active
  useEffect(() => {
    if (!currentSession) return;
    const id = setInterval(() => refreshQuota(currentSession), 30000);
    return () => clearInterval(id);
  }, [currentSession]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamBuffer]);

  const openSession = async (sid) => {
    setCurrentSession(sid);
    const { data } = await api.get(`/chat/messages/${sid}`);
    setMessages(data.messages || []);
  };

  const newChat = () => {
    setCurrentSession(null);
    setMessages([]);
    setStreamBuffer("");
  };

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || streaming) return;
    setError("");
    setInput("");

    // Pre-check: if quota exceeded, open paywall before streaming
    if (quota && !quota.allowed) {
      setAdOpen(true);
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setStreaming(true);
    setStreamBuffer("");

    const controller = new AbortController();
    abortRef.current = controller;
    let sessionIdForChat = currentSession;
    let accumulated = "";

    const runStream = async () => {
      const resp = await fetch(`${BACKEND}/api/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionIdForChat, language: lang || user?.chat_language || "en" }),
        signal: controller.signal,
      });

      if (resp.status === 402) {
        const body = await resp.json().catch(() => ({}));
        if (body?.detail?.error === "chat_ad_required" || body?.detail?.error === "chat_quota_exceeded") {
          setQuota(body.detail.quota);
          setAdOpen(true);
          setMessages((prev) => prev.slice(0, -1));
          throw { adRequired: true };
        }
      }

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const evt of events) {
          const lines = evt.split("\n");
          let eventName = "message";
          let dataStr = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataStr += ln.slice(5).trim();
          }
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr);
            if (eventName === "meta" && data.session_id) sessionIdForChat = data.session_id;
            else if (eventName === "error") throw new Error(data.error || "Stream error");
            else if (eventName === "done") { /* finalised */ }
            else if (data.delta) {
              accumulated += data.delta;
              setStreamBuffer(accumulated);
            }
          } catch (parseErr) { /* skip malformed */ }
        }
      }

      setMessages((prev) => [...prev, { role: "assistant", content: accumulated }]);
      setStreamBuffer("");
      // Speak the reply aloud if voice-reply mode is on
      if (voiceReplyOn && accumulated) {
        speak(accumulated, lang).catch(() => { /* noop */ });
      }
      if (!currentSession && sessionIdForChat) {
        setCurrentSession(sessionIdForChat);
        loadSessions();
      } else if (currentSession) {
        refreshQuota(currentSession);
      }
    };

    try {
      await guard(runStream, "AI Astro Talk");
    } catch (err) {
      if (err?.quotaPaywall) {
        // already handled
      } else if (err?.name !== "AbortError") {
        setError(formatApiError(err.response?.data?.detail) || err.message || "Chat failed");
        setMessages((prev) => prev.slice(0, -1));
      }
      setStreamBuffer("");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid="chat-page">
      <aside className="glass rounded-2xl p-4 max-h-[80vh] flex flex-col">
        <Button
          onClick={newChat}
          data-testid="new-chat-btn"
          className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium mb-4 gap-2"
        >
          <Plus className="w-4 h-4" /> {t("chat.new")}
        </Button>
        <div className="text-xs uppercase tracking-wider text-amber-300 mb-2 px-1">{t("chat.sessions")}</div>
        <div className="overflow-auto space-y-1 flex-1">
          {sessions.length === 0 && (
            <div className="text-sm text-slate-500 italic px-1">{t("chat.no_sessions")}</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => openSession(s.session_id)}
              data-testid={`session-${s.session_id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                currentSession === s.session_id
                  ? "bg-amber-500/15 text-amber-300"
                  : "text-slate-300 hover:bg-white/5"
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{s.session_id.slice(0, 14)}…</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{s.count} {t("chat.msgs")}</div>
            </button>
          ))}
        </div>
      </aside>

      <Card className="glass border-white/10 flex flex-col max-h-[80vh] min-h-[600px]">
        <div className="p-5 border-b border-white/5 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" />
            <h2 className="font-serif text-xl text-slate-50">Cosomic Sage</h2>
            <span className="text-xs text-slate-500 ml-2">— {t("chat.subtitle")}</span>
          </div>
          {quota?.session_started && (
            <div data-testid="chat-quota-bar" className="flex items-center gap-2 text-xs">
              <Clock className="w-3.5 h-3.5 text-amber-300" />
              <span className={`${quota.remaining_seconds < 60 ? "text-rose-300" : "text-slate-300"}`}>
                {Math.max(0, Math.floor(quota.remaining_seconds / 60))}m {Math.max(0, quota.remaining_seconds % 60)}s {t("chat.time_left")}
              </span>
              {!quota.allowed && (
                <Button
                  size="sm"
                  onClick={() => setAdOpen(true)}
                  data-testid="chat-extend-trigger"
                  className="ml-2 h-6 px-3 text-xs bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full"
                >
                  {t("chat.watch_ad_extend") || "Watch Ad"}
                </Button>
              )}
            </div>
          )}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto p-5 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && !streaming && (
            <div className="text-center py-10">
              <Sparkles className="w-10 h-10 text-gold mx-auto mb-3 opacity-50" />
              <p className="text-slate-400 mb-5">{t("chat.intro")}</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`starter-${s.slice(0, 10)}`}
                    className="px-3 py-1.5 rounded-full text-sm border border-white/10 text-slate-300 hover:text-amber-300 hover:border-amber-500/40 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}`}>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
              </div>
            </div>
          ))}
          {streaming && (
            <div className="flex justify-start">
              <div className="chat-bubble-ai max-w-[80%] rounded-2xl px-4 py-3" data-testid="stream-bubble">
                {streamBuffer ? (
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{streamBuffer}<span className="animate-pulse text-amber-400">▋</span></div>
                ) : (
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {error && <div className="px-5 py-2 text-sm text-rose-400" data-testid="chat-error">{error}</div>}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="p-4 border-t border-white/5 flex gap-2"
        >
          {ttsSupported && (
            <Button
              type="button"
              onClick={toggleVoiceReply}
              data-testid="chat-voice-reply-toggle"
              variant="outline"
              className={`rounded-full h-10 w-10 p-0 flex-shrink-0 border-white/10 ${
                voiceReplyOn ? "bg-amber-500/15 border-amber-400/50 text-amber-300" : "text-slate-400 hover:text-amber-300"
              }`}
              title={voiceReplyOn ? "Voice reply ON — tap to turn off" : "Voice reply OFF — tap to hear the astrologer"}
            >
              {voiceReplyOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
          )}
          {sttSupported && (
            <Button
              type="button"
              onClick={toggleMic}
              data-testid="chat-mic-btn"
              variant="outline"
              className={`rounded-full h-10 w-10 p-0 flex-shrink-0 border-white/10 ${
                listening ? "bg-rose-500/20 border-rose-400/60 text-rose-300 animate-pulse" : "text-slate-400 hover:text-amber-300"
              }`}
              title={listening ? "Listening... tap to stop" : "Tap and speak"}
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
          )}
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={listening ? "Listening..." : t("chat.placeholder")}
            data-testid="chat-input"
            disabled={streaming}
            className="flex-1 bg-slate-900/50 border-white/10 text-white"
          />
          <Button
            type="submit"
            disabled={streaming || !input.trim()}
            data-testid="chat-send-btn"
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full px-5"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </Card>

      <RewardedAdModal
        open={adOpen}
        onClose={() => setAdOpen(false)}
        kind="chat"
        title="Unlock 10 more minutes of Astro Talk"
        description="Play a short ad to extend your AI Astro Talk session by 10 minutes."
        onRewarded={() => refreshQuota(currentSession)}
      />
    </div>
  );
}
